import { useEffect, useState } from "react";

const SIZES = {
  xs: "h-6 w-6 text-2xs",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-lg",
  xl: "h-[72px] w-[72px] text-2xl",
};

// The API serializes user avatars as `{ url, thumb }` (from the model's
// avatarUrl()/avatarThumb()); older payloads may carry a plain string.
const userImage = (user) => {
  const avatar = user?.avatar;
  if (typeof avatar === "string") return avatar;
  return avatar?.url || avatar?.thumb || null;
};

const userName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ");

const initialsOf = (user, name) => {
  if (user?.firstName || user?.lastName) {
    return `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`;
  }
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0][0];
  return `${parts[0][0]}${parts[parts.length - 1][0]}`;
};

/**
 * Avatar — user photo with an initials fallback on a brand tint.
 *
 * @param {object} props
 * @param {object} [props.user] API user (`firstName`, `lastName`, `avatar: {url, thumb} | string`).
 * @param {string} [props.src] Image URL (overrides the user's avatar).
 * @param {string} [props.name] Display name used for alt text and initials (overrides the user's name).
 * @param {"xs"|"sm"|"md"|"lg"|"xl"} [props.size="xl"] 24 / 32 / 40 / 56 / 72 px.
 * @param {boolean} [props.noBorder=false] Drops the 2px separating border.
 * @param {boolean} [props.decorative=false] Hides the avatar from assistive tech (e.g. beside a heading
 *   that already names the person).
 * @param {string} [props.className]
 */
const Avatar = ({
  user,
  src,
  name,
  size = "xl",
  noBorder = false,
  decorative = false,
  className,
}) => {
  const imageUrl = src || userImage(user);
  const displayName = name || userName(user);
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [imageUrl]);

  if (!user && !src && !name) return null;

  const classes = [
    SIZES[size] || SIZES.xl,
    "shrink-0 rounded-full",
    noBorder ? "" : "border-2 border-surface-subtle",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt={decorative ? "" : displayName}
        aria-hidden={decorative || undefined}
        onError={() => setFailed(true)}
        className={`${classes} object-cover`}
      />
    );
  }

  return (
    <div
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : displayName || undefined}
      aria-hidden={decorative || undefined}
      className={`${classes} flex items-center justify-center bg-brand-100 font-medium uppercase text-brand-800 dark:bg-brand-900/40 dark:text-brand-100`}
    >
      <span aria-hidden="true">{initialsOf(name ? null : user, displayName)}</span>
    </div>
  );
};

export default Avatar;
